import React, { FC, useMemo, useState } from 'react';
import ActionList from '@components/ActionsMenu/ActionList';
import { IActionItem } from '@components/ActionsMenu/types';
import useStore from '@components/MainStage/store';
import { Modes } from '@constants/canvas/layers';
import { useProject } from '@context/Project/ProjectProvider';
import { useToast } from '@context/Toast/ToastProvider';
import useAssets from '@hooks/useAssets';
import useSaveEntitiesResponse from '@hooks/useSaveEntitiesResponse';
import { useUpdateNodesActionMutation } from '@store/services/nodes';
import {
  INodeActionOutcome,
  NodeActionResult,
  NodeDTO,
} from '@store/services/nodes/types';
import {
  getStatusOptionAvailability,
  NodeActionConfig,
  StatusOptionUnavailableReason,
} from '@utils/nodeActions';

import { NodeActionConfirmModal } from './NodeActionConfirmModal';

const DISABLED_STATUS_TOOLTIP =
  'This status cannot be applied to the selected entities.';

type Props = {
  config: NodeActionConfig;
  nodeIds: string[];
  onCloseMenu: () => void;
};

export const NodeActionSubmenu: FC<Props> = ({
  config,
  nodeIds,
  onCloseMenu,
}) => {
  const storeNodes = useStore((s) => s.nodes);
  const {
    toolbox: { mode },
  } = useProject();
  const resolvedMode = mode ?? Modes.BusinessAlignment;
  const { selectedAssetId } = useAssets();
  const { showToast } = useToast();
  const { saveResponseToRedux } = useSaveEntitiesResponse();
  const [updateNodesAction, { isLoading }] = useUpdateNodesActionMutation();

  const [pending, setPending] = useState<{
    value: string;
    outcomes: INodeActionOutcome[];
  } | null>(null);

  const nodeIdsKey = nodeIds.join(',');
  const nodes = useMemo(
    () =>
      nodeIds
        .map(
          (id) =>
            storeNodes.find((node) => node.id === id)?.data?.dto as
              | NodeDTO
              | undefined,
        )
        .filter((dto): dto is NodeDTO => Boolean(dto)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodeIdsKey, storeNodes],
  );
  const nameById = useMemo(
    () => new Map(nodes.map((node) => [node.id, node.name])),
    [nodes],
  );

  const run = async (value: string, force: boolean) => {
    const response = await updateNodesAction({
      nodeIds,
      actionType: config.actionType,
      value,
      mode: resolvedMode,
      assetId: selectedAssetId || undefined,
      force,
    }).unwrap();

    if (!response.applied) {
      setPending({ value, outcomes: response.outcomes });
      return;
    }
    saveResponseToRedux(response);
    showToast(config.successMessage, 'success');
    setPending(null);
    if (!config.keepMenuOpenOnApply) {
      onCloseMenu();
    }
  };

  const items: IActionItem[] = (config.statusOptions ?? []).map((option) => {
    const availability = getStatusOptionAvailability(
      nodes,
      resolvedMode,
      option.value,
      config,
    );
    const timelineTooltip =
      !availability.available &&
      availability.reason === StatusOptionUnavailableReason.TimelineConflict
        ? config.blockedValueTooltip?.(option.value)
        : undefined;

    return {
      text: option.label,
      disabled: isLoading || !availability.available,
      tooltip: availability.available
        ? undefined
        : timelineTooltip ?? DISABLED_STATUS_TOOLTIP,
      onClick: () => run(option.value, false),
    };
  });

  const toItem = (outcome: INodeActionOutcome) => ({
    id: outcome.node_id,
    name: nameById.get(outcome.node_id) ?? outcome.node_id,
  });

  const pendingOutcomes = pending?.outcomes ?? [];

  return (
    <>
      <ActionList items={items} />

      <NodeActionConfirmModal
        open={pending !== null}
        title={config.menuLabel}
        text={config.menuText}
        toChange={pendingOutcomes
          .filter(
            (o) =>
              o.result === NodeActionResult.Updated ||
              o.result === NodeActionResult.Skipped,
          )
          .map(toItem)}
        toChangeTitle={config.menuToChangeTitle}
        skipped={pendingOutcomes
          .filter(
            (o) =>
              o.result === NodeActionResult.Ineligible ||
              o.result === NodeActionResult.Blocked,
          )
          .map(toItem)}
        toSkipTitle={config.menuToSkipTitle}
        confirmName={config.menuConfirmName}
        onBack={() => setPending(null)}
        onConfirm={() => {
          if (pending && !isLoading) run(pending.value, true);
        }}
      />
    </>
  );
};
